import { queryClient } from 'Context/QueryClientContext';
import { Dispatch } from 'redux';
import { useMutation } from '@tanstack/react-query';
import { ActivitiesApi } from 'Data/ActivitiesApi';
import { INotificationInAppTopic } from 'Data/interfaces/Notification/INotification';
import {
  NOTIFICATIONS_PAGE_QUERY_KEY,
  NOTIFICATIONS_IN_APP_TOPIC_QUERY_KEY,
} from 'Data/keys/QueryKeys';
import { IReadCommentsRequest } from 'Data/interfaces/Activities/IComments';
import { ActivitiesActions } from 'Store/Activities/Activities.actions';

export const useQueryReadComments = (dispatch: Dispatch) => {
  const { mutate, isPending } = useMutation({
    mutationFn: (variables: IReadCommentsRequest) => {
      return ActivitiesApi.readComments(variables.topicId);
    },
    onMutate: async (variables) => {
      const previousInAppTopicsNotifications = queryClient.getQueryData<INotificationInAppTopic[]>(
        [NOTIFICATIONS_IN_APP_TOPIC_QUERY_KEY, Number(variables.csId)],
      );
      if (previousInAppTopicsNotifications !== undefined) {
        queryClient.setQueryData<INotificationInAppTopic[]>(
          [NOTIFICATIONS_IN_APP_TOPIC_QUERY_KEY, Number(variables.csId)],
          (oldData) => {
            const newNotifications = oldData?.map((notification) => {
              if (notification.TopicId === variables.topicId) {
                return {
                  TopicId: notification.TopicId,
                  NotificationIds: notification.NotificationIds,
                  HasCommentUnread: false,
                };
              }
              return notification;
            });
            const filteredNewNotifications = newNotifications?.filter((notification) => {
              return (notification.NotificationIds &&
                notification.NotificationIds.length > 0) ||
                notification.HasCommentUnread;
            });
            return filteredNewNotifications;
          },
        );
      }

      return { previousInAppTopicsNotifications };
    },
    onSuccess: async () => {
      dispatch(ActivitiesActions.readCommentsSuccess());
      await queryClient.invalidateQueries({
        queryKey: [NOTIFICATIONS_PAGE_QUERY_KEY],
      });
    },
    onError: async (_error, variables, context) => {
      await queryClient.cancelQueries({
        queryKey: [NOTIFICATIONS_IN_APP_TOPIC_QUERY_KEY, Number(variables.csId)],
        exact: true,
      });

      if (context?.previousInAppTopicsNotifications !== undefined) {
        queryClient.setQueryData(
          [NOTIFICATIONS_IN_APP_TOPIC_QUERY_KEY, Number(variables.csId)],
          context?.previousInAppTopicsNotifications,
        );
      }
    },
  });

  return {
    readComments: mutate,
    isLoading: isPending,
  };
};
